//Template 7
"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ImageCropModal } from "@/components/ui/ImageCropModal";
import {
  MapPin,
  Bed,
  Bath,
  Square,
  Phone,
  Mail,
  Upload,
  Image as ImageIcon,
  Zap,
  Crop,
  X,
  Tag,
  Check,
  Loader2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import useTemplateBase from "./TemplateBase";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";

interface Property {
  id: number;
  propertyListType?: { id: number; list_name: string; custom_template_data?: Record<string, any> };
  title: string;
  listing_id: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  price?: number | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  area?: number | null;
  description?: string | null;
  text_section?: string | null;
  image?: string | null;
  original_image?: string | null;
  photos?: string[] | null;
  // Keyed by slot name (e.g. "photo2"), not array position — see TemplateBase.tsx's updatePhotoAtIndex.
  original_photos?: Record<string, string> | null;
  is_open_house?: boolean;
}

// ─── Helper: route already-saved (non data:/blob:) S3 image URLs through the
// backend image proxy before feeding them to the crop modal's <canvas>, so the
// export doesn't fail with a cross-origin "tainted canvas" error. Mirrors the
// same helper in Template1.tsx.
const getEditableImageUrl = (url: string): string => {
  if (!url || url.startsWith("data:") || url.startsWith("blob:")) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    const needsProxy =
      parsed.hostname.includes("amazonaws.com") ||
      parsed.hostname.includes("mylistiq-production.s3");
    if (!needsProxy) return url;
    return `${endpoints.image_proxy}?url=${encodeURIComponent(url)}`;
  } catch {
    return url;
  }
};

interface PropertyList {
  id: number;
  list_name: string;
}

interface KeyFeature {
  id?: number;
  feature_name: string;
  propetyId: number;
}

interface Template7Props {
  property?: Property | null;
  propertyLists?: PropertyList[];
  keyFeatures?: KeyFeature[];
  onDirtyChange?: (dirty: boolean) => void;
  onChange?: (updatedFields: Partial<Template7FlyerData>) => void;
  onPropertyListChange?: (selectedList: string) => void;
  selectedPropertyList?: string;
  onPropertyListCreated?: (newList: PropertyList) => void;
}

interface FeatureItem {
  id?: number;
  feature_name: string;
}

interface Template7FlyerData {
  tag: string;
  price: number | null;
  headline: string;
  address: string;
  bedrooms: number | null;
  bathrooms: number | null;
  sqft: number | null;
  description: string;
  features: FeatureItem[];
  agentName?: string;
  agentPhone?: string;
  agentEmail?: string;
  heroImage: string;
  photo2?: string;
  photo3?: string;
  photo4?: string;
  list_name?: string;
}

const Template6: React.FC<Template7Props> = ({
  property,
  propertyLists = [],
  keyFeatures = [],
  onDirtyChange,
  onChange,
  onPropertyListChange,
  selectedPropertyList: externalSelectedList,
  onPropertyListCreated,
}) => {
  const { toast } = useToast();

  const [cropModal, setCropModal] = useState<{ open: boolean; imageUrl: string }>({
    open: false,
    imageUrl: "",
  });

  // Tracks the untouched "original" hero photo for this editing session, so
  // Edit Image always re-crops from the full source rather than whatever is
  // currently displayed. Mirrors the same state in Template1.tsx.
  const [originalHeroImage, setOriginalHeroImage] = useState<string>(
    property?.original_image || ""
  );

  useEffect(() => {
    setOriginalHeroImage(property?.original_image || "");
  }, [property?.id]);

  const photo2Ref = useRef<HTMLInputElement>(null);
  const photo3Ref = useRef<HTMLInputElement>(null);
  const photo4Ref = useRef<HTMLInputElement>(null);

  // Custom list creation state
  const [isCustomList, setIsCustomList] = useState<boolean>(false);
  const [customListName, setCustomListName] = useState<string>("");
  const [isSavingCustomList, setIsSavingCustomList] = useState(false);
  const [localPropertyLists, setLocalPropertyLists] = useState<PropertyList[]>(propertyLists);

  // Custom fields editor state
  const [editingCustom6, setEditingCustom6] = useState(false);
  const [customFields6, setCustomFields6] = useState<{ key: string; value: string }[]>([]);

  const {
    flyerData,
    setFlyerData,
    selectedPropertyList,
    fileInputRef,
    agentData,
    handleInputChange,
    addFeature,
    handleFeatureChange,
    handleFeatureDelete,
    updatePropertyList,
    updatePhotoAtIndex,
    uploadCroppedPhoto,
    saveCustomTemplateData,
  } = useTemplateBase({
    property,
    propertyLists: localPropertyLists,
    keyFeatures,
    onDirtyChange,
    onChange,
    onPropertyListChange,
  });

  // Sync local property lists when prop changes
  useEffect(() => {
    setLocalPropertyLists(propertyLists);
  }, [propertyLists]);

  // Initialize custom fields from property
  useEffect(() => {
    const existingCustomData = property?.propertyListType?.custom_template_data?.template6 || [];
    setCustomFields6(Array.isArray(existingCustomData) ? existingCustomData : []);
  }, [property?.propertyListType?.custom_template_data?.template6]);

  // Determine currently selected list
  const selectedList =
    externalSelectedList ??
    selectedPropertyList ??
    property?.propertyListType?.list_name ??
    "Just Listed";

  // Combine prop lists with local lists, provide defaults if empty
  const allLists =
    localPropertyLists.length > 0
      ? localPropertyLists
      : [
          { id: 1, list_name: "Just Listed" },
          { id: 2, list_name: "Priced to Sell" },
          { id: 3, list_name: "Open House" },
          { id: 4, list_name: "New Price" },
        ];

  // Custom fields editor functions
  const addCustomField6 = () => setCustomFields6((p) => [...p, { key: "", value: "" }]);
  const updateCustomField6 = (i: number, field: "key" | "value", v: string) => {
    setCustomFields6((p) => {
      const n = [...p];
      n[i] = { ...n[i], [field]: v };
      return n;
    });
  };
  const removeCustomField6 = (i: number) => setCustomFields6((p) => p.filter((_, idx) => idx !== i));

  const saveCustomForTemplate6 = async () => {
    try {
      if (typeof saveCustomTemplateData === "function") {
        await saveCustomTemplateData("template6", customFields6.filter((f) => f.key || f.value));
      } else {
        const token = localStorage.getItem("access_token");
        if (property?.propertyListType?.id) {
          await axiosInstance.patch(
            `${endpoints.propertylisttype}${property.propertyListType.id}/`,
            {
              custom_template_data: {
                ...(property.propertyListType.custom_template_data || {}),
                template6: customFields6,
              },
            },
            { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
          );
        }
      }
      setEditingCustom6(false);
      toast({ title: "Saved", description: "Custom fields saved" });
    } catch (err) {
      console.error(err);
      toast({ title: "Error", description: "Failed saving custom fields", variant: "destructive" });
    }
  };

  // === OPEN HOUSE LOGIC ===
  const [isOpenHouseConfirmed, setIsOpenHouseConfirmed] = useState<boolean>(
    property?.is_open_house ?? false
  );

  const handleOpenHouseChoice = async (confirmed: boolean) => {
    if (!property?.id) return;

    setIsOpenHouseConfirmed(confirmed);

    try {
      await axiosInstance.patch(`${endpoints.properties}${property.id}/`, {
        is_open_house: confirmed,
      });

      toast({
        title: confirmed ? "Open House Active" : "Open House Disabled",
        description: confirmed
          ? "Special styling enabled"
          : "Open House mode turned off",
        variant: "default",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to save choice",
        variant: "destructive",
      });
      setIsOpenHouseConfirmed(!confirmed);
    }
  };

  useEffect(() => {
    setIsOpenHouseConfirmed(property?.is_open_house ?? false);
  }, [property?.is_open_house]);

  // === SAVE CUSTOM LIST - CREATES NEW PropertyListType IN DATABASE ===
  const saveCustomList = async () => {
    const trimmedName = customListName.trim();

    if (!trimmedName) {
      toast({
        title: "Error",
        description: "Please enter a name for your custom tag",
        variant: "destructive",
      });
      return;
    }

    if (!property?.id) {
      toast({
        title: "Error",
        description: "No property selected. Please select a property first.",
        variant: "destructive",
      });
      return;
    }

    // Check if this list name already exists locally (case-insensitive)
    const existingList = localPropertyLists.find(
      (l) => l.list_name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (existingList) {
      // Just select existing instead of creating duplicate
      updatePropertyList(existingList.list_name, existingList.id);
      setIsCustomList(false);
      setCustomListName("");

      toast({
        title: "List Selected",
        description: `"${existingList.list_name}" already exists and has been selected`,
      });
      return;
    }

    setIsSavingCustomList(true);

    try {
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Step 1: Create new PropertyListType in database
      console.log("Creating new PropertyListType:", trimmedName);
      const createResponse = await axiosInstance.post(
        endpoints.propertylisttype,
        { list_name: trimmedName },
        { headers }
      );

      console.log("PropertyListType created:", createResponse.data);

      const newListType: PropertyList = {
        id: createResponse.data.id,
        list_name: createResponse.data.list_name,
      };

      // Step 2: Update local state with new list
      setLocalPropertyLists((prev) => {
        const exists = prev.some((l) => l.id === newListType.id);
        if (exists) return prev;
        return [...prev, newListType];
      });

      // Step 3: Reset custom input mode
      setIsCustomList(false);
      setCustomListName("");

      // Step 4: Notify parent component if callback provided
      onPropertyListCreated?.(newListType);
      // Step 5: Update selected list + persist it (single PATCH, owned upstream).
      // Pass the id explicitly — propertyLists in the parent may not have this
      // brand-new entry yet, so a name-only lookup there could miss it.
      updatePropertyList(newListType.list_name, newListType.id);

      toast({
        title: "Custom Tag Created",
        description: `"${newListType.list_name}" has been created and saved to database`,
      });
    } catch (err: any) {
      console.error("Failed to create custom list:", err);

      const errorMessage =
        err?.response?.data?.list_name?.[0] ||
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        "Failed to create custom tag";

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSavingCustomList(false);
    }
  };

  // Cancel custom list creation
  const cancelCustomList = () => {
    setIsCustomList(false);
    setCustomListName("");
  };

  // === Image Upload Logic ===
  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "heroImage" | "photo2" | "photo3" | "photo4"
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (field !== "heroImage") e.target.value = "";

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid File",
        description: "Please upload an image",
        variant: "destructive",
      });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Max 5MB",
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      setFlyerData((prev) => ({ ...prev, [field]: dataUrl }));
      if (field === "heroImage") {
        // This freshly-picked photo becomes the new "original" for this
        // session immediately, so Edit Image always has an untouched source
        // to re-crop from, right from this point on. Mirrors Template1.tsx.
        setOriginalHeroImage(dataUrl);
      }

      try {
        const token = localStorage.getItem("access_token");
        const formData = new FormData();
        formData.append("image", file);

        if (field === "heroImage") {
          // Persist this same freshly-picked photo as the untouched
          // "original" separately from the displayed image, so Edit Image
          // can always re-crop from the full source later instead of
          // re-cropping an already-cropped result. Piggybacks on the PATCH
          // already being sent here so there's no extra request.
          formData.append("original_image", file);

          onDirtyChange?.(true);
          onChange?.({ heroImage: dataUrl });

          await axiosInstance.patch(
            `${endpoints.properties}${property?.id}/`,
            formData,
            {
              headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                "Content-Type": "multipart/form-data",
              },
            }
          );
          toast({ title: "Success", description: "Main photo updated" });
          onDirtyChange?.(false);
        } else {
          const res = await axiosInstance.post(
            endpoints.mls_uploads,
            formData,
            {
              headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                "Content-Type": "multipart/form-data",
              },
            }
          );
          if (res?.data?.url) {
            updatePhotoAtIndex(
              res.data.url,
              field as "photo2" | "photo3" | "photo4"
            );
            toast({ title: "Success", description: "Photo uploaded",duration: 1000 });
          }
        }
      } catch (err) {
        toast({
          title: "Error",
          description: "Upload failed",
          variant: "destructive",
          duration: 1000,
        });
      }
    };
    reader.readAsDataURL(file);
  };

  // ─── Edit Image: reposition/zoom the ORIGINAL hero photo (no file picker) ──
  const EDIT_CROP_W = 400;
  const EDIT_CROP_H = 267; // matches the hero box's ~h-64/80/96 (3:2-ish) aspect ratio

  const openEditImage = async () => {
    if (!flyerData.heroImage) return;

    let originalSource = originalHeroImage;

    // This component may remount when the hero image changes, resetting local
    // state — so an empty originalHeroImage here doesn't necessarily mean
    // "never captured". Check the server (unaffected by remounts) before
    // assuming there isn't one and falling back to the current display.
    if (!originalSource && property?.id) {
      try {
        const token = localStorage.getItem("access_token");
        const res = await axiosInstance.get(
          `${endpoints.properties}${property.id}/`,
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
        originalSource = res?.data?.original_image || "";
      } catch (err) {
        console.error("Failed to check for existing original image:", err);
      }
    }

    if (!originalSource) {
      // Truly never captured yet (new property, or MLS import). Self-heal
      // from the current image so this and every future edit has a stable,
      // full source to re-crop from instead of the current display.
      originalSource = flyerData.heroImage;
      if (property?.id && !flyerData.heroImage.startsWith("data:")) {
        const token = localStorage.getItem("access_token");
        axiosInstance
          .patch(
            `${endpoints.properties}${property.id}/`,
            { original_image: flyerData.heroImage },
            { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
          )
          .catch((err) => {
            console.error("Failed to seed original image:", err);
          });
      }
    }

    setOriginalHeroImage(originalSource);
    setCropModal({ open: true, imageUrl: getEditableImageUrl(originalSource) });
  };

  const handleEditImageConfirm = (croppedDataUrl: string) => {
    setFlyerData((prev) => ({ ...prev, heroImage: croppedDataUrl }));
    onDirtyChange?.(true);
    onChange?.({ heroImage: croppedDataUrl });
    setCropModal({ open: false, imageUrl: "" });
  };

  const handleEditImageCancel = () => {
    setCropModal({ open: false, imageUrl: "" });
  };

  // ─── Edit Image for secondary/gallery photos (same non-destructive pattern
  // as the hero photo above, reusing the shared updatePhotoAtIndex/
  // uploadCroppedPhoto helpers from TemplateBase so original-capture stays
  // centralized) ──
  const SECONDARY_CROP_W = 260;
  const SECONDARY_CROP_H = 192; // matches the h-48 (192px) secondary-photo box aspect ratio

  const [secondaryCropModal, setSecondaryCropModal] = useState<{
    open: boolean;
    imageUrl: string;
    field: "photo2" | "photo3" | "photo4" | null;
  }>({ open: false, imageUrl: "", field: null });

  const openEditSecondaryPhoto = async (
    field: "photo2" | "photo3" | "photo4",
    currentSrc: string
  ) => {
    if (!currentSrc) return;

    // Keyed by slot name ("photo2"/"photo3"/"photo4"), not array position —
    // see TemplateBase.tsx's updatePhotoAtIndex for why.
    let originalSource = (flyerData.original_photos && flyerData.original_photos[field]) || "";

    if (!originalSource && property?.id) {
      try {
        const token = localStorage.getItem("access_token");
        const res = await axiosInstance.get(
          `${endpoints.properties}${property.id}/`,
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
        originalSource = res?.data?.original_photos?.[field] || "";
      } catch (err) {
        console.error("Failed to check for existing original photo:", err);
      }
    }

    if (!originalSource) {
      // Truly never captured yet. Self-heal by re-saving the current photo
      // through updatePhotoAtIndex (captureOriginal defaults to true), so
      // this and every future edit has a stable, full source to re-crop
      // from instead of the current display.
      originalSource = currentSrc;
      if (!currentSrc.startsWith("data:")) {
        updatePhotoAtIndex(currentSrc, field).catch((err) => {
          console.error("Failed to seed original photo:", err);
        });
      }
    }

    setSecondaryCropModal({
      open: true,
      imageUrl: getEditableImageUrl(originalSource),
      field,
    });
  };

  const handleSecondaryEditConfirm = async (croppedDataUrl: string) => {
    const field = secondaryCropModal.field;
    if (!field) return;
    try {
      const newUrl = await uploadCroppedPhoto(croppedDataUrl);
      await updatePhotoAtIndex(newUrl, field, { captureOriginal: false });
      toast({ title: "Success", description: "Photo updated" });
    } catch (err) {
      console.error("Failed to save edited photo:", err);
      toast({
        title: "Error",
        description: "Failed to save edited photo",
        variant: "destructive",
      });
    } finally {
      setSecondaryCropModal({ open: false, imageUrl: "", field: null });
    }
  };

  const handleSecondaryEditCancel = () => {
    setSecondaryCropModal({ open: false, imageUrl: "", field: null });
  };

  const handleRemovePhoto = (
    field: "heroImage" | "photo2" | "photo3" | "photo4"
  ) => {
    setFlyerData((prev) => ({
      ...prev,
      [field]: field === "heroImage" ? "" : undefined,
    }));
    onDirtyChange?.(true);
    onChange?.({ [field]: field === "heroImage" ? "" : undefined });
    toast({ title: "Removed", description: "Photo removed" });
  };

  return (
    <div className="bg-gradient-to-br from-[#f8fafc] to-[#f1f5f9] min-h-screen font-arial relative">
      {/* Custom Fields Editor Modal */}
      {editingCustom6 && (
        <div className="absolute right-6 top-20 z-50 bg-white p-4 rounded-lg shadow-xl w-80 border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <strong className="text-gray-900">Custom Fields</strong>
            <div className="flex gap-2">
              <button
                onClick={addCustomField6}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Add
              </button>
              <button
                onClick={() => setEditingCustom6(false)}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Close
              </button>
            </div>
          </div>
          <div className="max-h-48 overflow-auto">
            {customFields6.map((f, idx) => (
              <div key={idx} className="flex gap-2 items-center mb-2">
                <input
                  value={f.key}
                  onChange={(e) => updateCustomField6(idx, "key", e.target.value)}
                  placeholder="Key"
                  className="border border-gray-300 px-2 py-1 flex-1 text-sm rounded"
                />
                <input
                  value={f.value}
                  onChange={(e) => updateCustomField6(idx, "value", e.target.value)}
                  placeholder="Value"
                  className="border border-gray-300 px-2 py-1 flex-1 text-sm rounded"
                />
                <button
                  onClick={() => removeCustomField6(idx)}
                  className="text-red-600 hover:text-red-800"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={saveCustomForTemplate6}
              className="bg-green-600 text-white px-4 py-1.5 rounded text-sm hover:bg-green-700"
            >
              Save
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="px-8 pt-6 pb-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center shadow-lg">
            <Zap className="h-6 w-6 text-white" />
          </div>
          <span className="text-lg font-bold text-gray-800">
            {agentData?.company || "Premier Realty Group"}
          </span>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <Tag className="h-5 w-5 text-red-500" />

          {/* Property List Selector */}
          {isCustomList ? (
            // Custom list input mode
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2">
              <input
                type="text"
                value={customListName}
                onChange={(e) => setCustomListName(e.target.value.slice(0, 20))}
                placeholder="Custom tag (max 20 chars)"
                maxLength={20}
                className="bg-transparent text-gray-800 placeholder-gray-400 px-2 py-1 text-sm w-28 md:w-36 outline-none border-none"
                autoFocus
                disabled={isSavingCustomList}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !isSavingCustomList) {
                    e.preventDefault();
                    saveCustomList();
                  }
                  if (e.key === "Escape") {
                    cancelCustomList();
                  }
                }}
              />
              <button
                onClick={saveCustomList}
                disabled={!customListName.trim() || isSavingCustomList}
                className="p-1.5 bg-red-100 rounded hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Save custom tag"
              >
                {isSavingCustomList ? (
                  <Loader2 className="h-4 w-4 text-red-600 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 text-red-600" />
                )}
              </button>
              <button
                onClick={cancelCustomList}
                disabled={isSavingCustomList}
                className="p-1.5 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 transition-colors"
                title="Cancel"
              >
                <X className="h-4 w-4 text-gray-600" />
              </button>
            </div>
          ) : (
            // Normal dropdown mode
            <select
              value={selectedList}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "__custom__") {
                  setIsCustomList(true);
                  setCustomListName("");
                } else {
                  // Persistence (the PATCH request) happens once, upstream in the
                  // parent's onPropertyListChange handler — see updatePropertyList
                  // in TemplateBase.
                  updatePropertyList(value);
                }
              }}
              className="bg-white/90 backdrop-blur-sm border border-gray-200 text-gray-800 px-4 py-2 rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-red-400"
            >
              <option value="">-- Select --</option>
              {allLists
                .filter((l) => l.list_name !== "Open House")
                .map((l) => (
                  <option key={l.id} value={l.list_name}>
                    {l.list_name}
                  </option>
                ))}
              <option value="__custom__" className="font-medium">
                + Add Custom Tag
              </option>
            </select>
          )}

          {/* + CUSTOM Button for custom fields */}
          <button
            onClick={() => setEditingCustom6(true)}
            className="bg-red-500 hover:bg-red-600 text-white text-sm font-semibold px-4 py-2 rounded-xl shadow transition"
          >
            + CUSTOM
          </button>

          {/* OPEN HOUSE BADGE */}
          {isOpenHouseConfirmed && (
            <div className="flex items-center gap-2 px-4 py-2 bg-green-500 rounded-xl shadow">
              <span className="text-xs md:text-sm font-bold text-white">
                OPEN HOUSE
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Hero Image + Price + Address */}
      <div className="px-8 pb-6">
        <div className="relative rounded-2xl overflow-hidden shadow-2xl group h-64 md:h-80 lg:h-96">
          {flyerData.heroImage ? (
            <img
              src={flyerData.heroImage}
              alt="Main"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          ) : (
            <div className="bg-gray-200 w-full h-full flex items-center justify-center">
              <ImageIcon className="h-20 w-20 text-gray-400" />
            </div>
          )}

          {/* Upload overlay */}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="bg-white/20 backdrop-blur text-white hover:bg-white/30"
            >
              <Upload className="h-4 w-4 mr-2" /> Change
            </Button>
            <Button
              onClick={openEditImage}
              className="bg-white/20 backdrop-blur text-white hover:bg-white/30"
            >
              <Crop className="h-4 w-4 mr-2" /> Edit
            </Button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFileChange(e, "heroImage")}
          />
          <ImageCropModal
            open={cropModal.open}
            imageUrl={cropModal.imageUrl}
            title="Adjust Property Photo"
            outputSize={700}
            cropW={EDIT_CROP_W}
            cropH={EDIT_CROP_H}
            onConfirm={handleEditImageConfirm}
            onCancel={handleEditImageCancel}
          />

          {/* Price & Address overlay */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-8">
            <div className="flex items-baseline gap-2">
              <span className="text-white/80 text-2xl">$</span>
              <input
                type="text"
                value={
                  flyerData.price
                    ? Number(flyerData.price).toLocaleString()
                    : ""
                }
                onChange={(e) => {
                  const num = e.target.value.replace(/\D/g, "");
                  handleInputChange("price", num ? Number(num) : null);
                }}
                className="bg-transparent text-5xl font-bold text-white border-none outline-none w-full"
                placeholder="425,000"
              />
            </div>
            <div className="flex items-center gap-3 mt-3">
              <MapPin className="h-5 w-5 text-white/70 flex-shrink-0" />
              <input
                type="text"
                value={flyerData.address}
                onChange={(e) => handleInputChange("address", e.target.value)}
                className="bg-transparent text-white/90 text-lg border-none outline-none w-full"
                placeholder="123 Luxury Lane, Beverly Hills, CA"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Photos */}
      <div className="px-8 pb-6">
        <div className="grid grid-cols-3 gap-4">
          {[photo2Ref, photo3Ref, photo4Ref].map((ref, i) => {
            const field = `photo${i + 2}` as "photo2" | "photo3" | "photo4";
            const src = flyerData[field];
            return (
              <div
                key={field}
                className="relative rounded-xl overflow-hidden shadow-lg group"
              >
                {src ? (
                  <img src={src} alt="" className="w-full h-48 object-cover" />
                ) : (
                  <div className="bg-gray-200 h-48 flex items-center justify-center">
                    <ImageIcon className="h-12 w-12 text-gray-400" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                  <Button
                    size="sm"
                    onClick={() => ref.current?.click()}
                    className="bg-white/20 backdrop-blur text-white"
                  >
                    <Upload className="h-4 w-4 mr-1" /> Change
                  </Button>
                  {src && (
                    <Button
                      size="sm"
                      onClick={() => openEditSecondaryPhoto(field, src)}
                      className="bg-white/20 backdrop-blur text-white"
                    >
                      <Crop className="h-4 w-4 mr-1" /> Edit
                    </Button>
                  )}
                </div>
                <input
                  ref={ref}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileChange(e, field)}
                />
              </div>
            );
          })}
        </div>
        <ImageCropModal
          open={secondaryCropModal.open}
          imageUrl={secondaryCropModal.imageUrl}
          title="Adjust Property Photo"
          outputSize={700}
          cropW={SECONDARY_CROP_W}
          cropH={SECONDARY_CROP_H}
          onConfirm={handleSecondaryEditConfirm}
          onCancel={handleSecondaryEditCancel}
        />
      </div>

      {/* Quick Stats */}
      <div className="px-8 pb-6">
        <div className="bg-white/80 backdrop-blur rounded-2xl shadow-md border border-gray-200 p-6">
          <div className="grid grid-cols-3 gap-8 text-center">
            {[
              {
                icon: Bed,
                field: "bedrooms",
                label: "Beds",
                color: "text-red-500",
              },
              {
                icon: Bath,
                field: "bathrooms",
                label: "Baths",
                color: "text-orange-500",
              },
              {
                icon: Square,
                field: "sqft",
                label: "Sq Ft",
                color: "text-purple-600",
              },
            ].map(({ icon: Icon, field, label, color }) => {
              const value = flyerData[field as keyof Template7FlyerData] as
                | number
                | null;
              return (
                <div key={field}>
                  <Icon className={`h-8 w-8 mx-auto mb-2 ${color}`} />
                  <input
                    type="number"
                    min="0"
                    value={value ?? ""}
                    onChange={(e) =>
                      handleInputChange(
                        field,
                        e.target.value ? Number(e.target.value) : null
                      )
                    }
                    className="text-3xl font-bold text-gray-800 bg-transparent text-center w-full border-none outline-none [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    placeholder="0"
                  />
                  <p className="text-sm text-gray-600 mt-1">{label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="px-8 pb-6">
        <textarea
          value={flyerData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
          rows={4}
          className="w-full bg-white/70 backdrop-blur border border-gray-300 rounded-xl p-5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
          placeholder="Stunning modern residence with breathtaking views..."
        />
      </div>

      {/* Key Features */}
      <div className="px-8 pb-6">
        <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-3">
          <Zap className="h-7 w-7 text-red-500" /> Key Features
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {flyerData.features.map((f, i) => (
            <div
              key={f.id ?? i}
              className="flex items-center gap-3 bg-white/80 backdrop-blur rounded-xl 
                   p-4 border border-gray-200 hover:border-red-300 
                   transition-all group min-h-[56px] w-full"
            >
              {/* Bullet */}
              <div className="w-3 h-3 rounded-full bg-gradient-to-r from-red-500 to-orange-500 flex-shrink-0" />

              {/* Text Input */}
              <input
                type="text"
                value={f.feature_name}
                onChange={(e) => handleFeatureChange(i, e.target.value)}
                className="flex-1 bg-transparent border-none outline-none 
                     text-gray-800 font-medium min-w-0"
                placeholder="Home Theater (max 30 chars)..."
                maxLength={30}
              />

              {/* Delete Button */}
              <button
                onClick={() => handleFeatureDelete(i)}
                className="text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          ))}

          {/* Add Feature Button */}
          <button
            onClick={addFeature}
            className="flex items-center justify-center bg-white/80 min-h-[56px] w-full 
                 border border-red-300 border-dashed rounded-xl 
                 hover:border-red-500 text-red-500 font-medium"
          >
            + Add Feature
          </button>
        </div>
      </div>

{/* Agent Section */}
<div className="px-8 pb-10">
  <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 text-white">
    <div className="flex flex-col md:flex-row items-center gap-8">

      {/* Avatar */}
      <div className="w-28 h-28 rounded-full bg-gray-700 ring-4 ring-white/20 overflow-hidden shadow-xl flex items-center justify-center">
        {agentData?.avatar ? (
          <img
            src={agentData.avatar}
            alt="Agent"
            className="w-full h-full object-cover"
          />
        ) : (
          <ImageIcon className="w-14 h-14 text-gray-500" />
        )}
      </div>

      {/* Agent Info */}
      <div className="text-center md:text-left flex-1">

        {/* Agent Name */}
        <input
          type="text"
          value={flyerData.agentName || ""}
          onChange={(e) => handleInputChange("agentName", e.target.value)}
          className="text-3xl font-bold bg-transparent border-none outline-none text-white placeholder-white/60 w-full"
          placeholder="Your Name"
        />

        {/* Company Name */}
        <input
          type="text"
          value={flyerData.companyName || ""}
          onChange={(e) => handleInputChange("companyName", e.target.value)}
          className="text-sm mt-1 text-white/70 bg-transparent border-none outline-none placeholder-white/40 w-full"
          placeholder="Company Name"
        />

        {/* Contact Info — STACKED */}
        <div className="flex flex-col gap-3 mt-5">

          {/* Phone */}
          <div className="flex items-center gap-3 justify-center md:justify-start">
            <Phone className="h-5 w-5 text-red-400" />
            <input
              type="text"
              value={flyerData.agentPhone || ""}
              onChange={(e) =>
                handleInputChange("agentPhone", e.target.value)
              }
              className="bg-transparent border-none outline-none text-white/90 placeholder-white/50"
              placeholder="(555) 123-4567"
            />
          </div>

          {/* Email — BELOW PHONE */}
          <div className="flex items-center gap-3 justify-center md:justify-start">
            <Mail className="h-5 w-5 text-red-400" />
            <input
              type="text"
              value={flyerData.agentEmail || ""}
              onChange={(e) =>
                handleInputChange("agentEmail", e.target.value)
              }
              className="bg-transparent border-none outline-none text-white/90 placeholder-white/50"
              placeholder="agent@email.com"
            />
          </div>

        </div>

      </div>
    </div>
  </div>
</div>


      {/* Custom fields (read-only display) from PropertyListType */}
      {property?.propertyListType?.custom_template_data?.template6 &&
        Array.isArray(property.propertyListType.custom_template_data.template6) &&
        property.propertyListType.custom_template_data.template6.length > 0 && (
          <div className="px-8 pb-6">
            <div className="bg-white/80 backdrop-blur rounded-lg p-4 border border-gray-200">
              <h4 className="font-semibold text-sm text-gray-800 mb-2">
                Custom Fields
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                {property.propertyListType.custom_template_data.template6.map(
                  (f: any, i: number) => (
                    <div key={i} className="flex justify-between">
                      <span className="font-medium">{f.key}</span>
                      <span>{f.value}</span>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default Template6;